import { useState } from 'react';
import { uploadReviewImage } from '../api/secureRequests';

interface UseReviewImageUploadProps {
  tempId: string;
  idToken: string;
}

export const useReviewImageUpload = ({ tempId, idToken }: UseReviewImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadImage = async (file: File): Promise<{ key: string; viewUrl: string; hash: string } | null> => {
    setUploadError(null);
    setUploading(true);

    try {
      // Validate file type
      if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
        throw new Error(`${file.name}: Only JPG and PNG images are supported`);
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`${file.name}: File size must be under 5MB`);
      }

      const result = await uploadReviewImage(tempId, file, idToken) as {
        key: string;
        viewUrl: string;
        hash: string;
      };

      if (!result || !result.key || !result.viewUrl) {
        throw new Error('Invalid response from upload');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      setUploadError(errorMessage);
      return null;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadImage,
    uploading,
    uploadError,
    clearError: () => setUploadError(null),
  };
};